import { useEffect, useState, useRef, type ChangeEvent, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useEditor } from '../../contexts/EditorContext';
import { useCompiler } from '../../contexts/CompilerContext';
import { ALICE } from '../../examples/test-keys';

interface PropertyInfo {
  name: string;
  type: { kind: string; name?: string };
  readonly: boolean;
}

/** Input that uses local state while editing and only commits on blur or Enter */
function DeferredInput({ value, onCommit, className }: {
  value: string;
  onCommit: (value: string) => void;
  className?: string;
}) {
  const [local, setLocal] = useState(value);
  const committed = useRef(value);

  useEffect(() => {
    if (value !== committed.current) {
      setLocal(value);
      committed.current = value;
    }
  }, [value]);

  const commit = () => {
    if (local !== committed.current) {
      committed.current = local;
      onCommit(local);
    }
  };

  return (
    <input
      type="text"
      value={local}
      onChange={(e: ChangeEvent<HTMLInputElement>) => setLocal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e: ReactKeyboardEvent) => { if (e.key === 'Enter') commit(); }}
      className={className}
    />
  );
}

/** Generate a sensible default value for a given Rúnar type */
function defaultForType(typeName: string): bigint | boolean | string {
  switch (typeName) {
    case 'bigint': return 0n;
    case 'boolean': return true;
    case 'PubKey': return ALICE.pubKey;
    case 'Ripemd160': return ALICE.pubKeyHash;
    case 'Sig': return '';
    case 'Sha256': return '00'.repeat(32);
    case 'ByteString': return '00';
    case 'Addr': return ALICE.pubKeyHash;
    default: return '00';
  }
}

export function ConstructorArgs() {
  const { constructorArgs, setConstructorArgs } = useEditor();
  const { result } = useCompiler();

  // Auto-detect constructor args from the compiled artifact's ABI.
  // Only show actual constructor parameters, not all properties
  // (properties with initializers in the constructor body are not params).
  useEffect(() => {
    if (!result?.artifact) return;

    const artifact = result.artifact as {
      abi?: { constructor?: { params?: Array<{ name: string; type: string }> } };
    };
    const params = artifact.abi?.constructor?.params;
    if (!params || params.length === 0) return;

    let changed = false;
    const next = { ...constructorArgs };

    for (const param of params) {
      if (!(param.name in next)) {
        next[param.name] = defaultForType(param.type);
        changed = true;
      }
    }

    // Remove args that are no longer constructor params
    for (const key of Object.keys(next)) {
      if (!params.some(p => p.name === key)) {
        delete next[key];
        changed = true;
      }
    }

    if (changed) {
      setConstructorArgs(next);
    }
  }, [result?.artifact]); // eslint-disable-line react-hooks/exhaustive-deps

  const entries = Object.entries(constructorArgs);
  if (entries.length === 0) return null;

  const updateArg = (key: string, raw: string) => {
    const next = { ...constructorArgs };

    if (raw === 'true') {
      next[key] = true;
    } else if (raw === 'false') {
      next[key] = false;
    } else if (/^\d+$/.test(raw)) {
      next[key] = BigInt(raw);
    } else {
      next[key] = raw;
    }

    setConstructorArgs(next);
  };

  const displayValue = (v: bigint | boolean | string): string => {
    if (typeof v === 'bigint') return v.toString();
    return String(v);
  };

  const typeLabel = (v: bigint | boolean | string): string => {
    if (typeof v === 'bigint') return 'bigint';
    if (typeof v === 'boolean') return 'bool';
    if (typeof v === 'string' && /^[0-9a-fA-F]*$/.test(v)) return 'hex';
    return 'string';
  };

  return (
    <div className="border-t border-border bg-surface px-3 py-1.5 shrink-0">
      <div className="text-[10px] text-text-tertiary uppercase tracking-wide mb-1">
        Constructor Args
      </div>
      <div className="space-y-1">
        {entries.map(([key, value]) => (
          <div key={key} className="flex items-center gap-2">
            <span className="text-xs text-text-secondary w-24 truncate shrink-0" title={key}>
              {key}
            </span>
            <span className="text-[10px] text-text-tertiary bg-surface-alt px-1 rounded shrink-0">
              {typeLabel(value)}
            </span>
            <DeferredInput
              value={displayValue(value)}
              onCommit={(raw) => updateArg(key, raw)}
              className="flex-1 min-w-0 h-6 px-1.5 text-xs font-mono bg-bg border border-border rounded
                         text-text-secondary focus:border-accent-500/50 focus:outline-none transition-colors"
            />
          </div>
        ))}
      </div>
    </div>
  );
}
